<template>
  <HpsfirProDrawer
    v-model:open="open"
    :title="title || ''"
    width="420px"
    max-width="calc(100vw - 32px)"
    mobile-height="75vh"
    mobile-max-height="75vh"
  >
    <div class="business-danger-dialog__content">
      <p class="business-danger-dialog__summary">
        {{ summaryLeadText }}
        <span class="business-danger-dialog__keyword">“{{ confirmKeyword }}”</span>
        后再点击确认。
      </p>
      <div class="business-danger-dialog__input-card">
        <div class="business-danger-dialog__input-top">
          <label class="business-danger-dialog__field-label" :for="inputId">输入确认词</label>
          <span class="business-danger-dialog__field-chip">{{ confirmKeyword }}</span>
        </div>
        <HpsfirInput
          :id="inputId"
          v-model="inputDate"
          :placeholder="inputPlaceholder"
          class="business-danger-dialog__input"
          autocomplete="off"
          @keyup.enter="canConfirm && handleOk()"
        />
        <p class="business-danger-dialog__hint">仅在输入内容与关键字完全一致时才可继续。</p>
      </div>
    </div>

    <template #footer>
      <div class="business-danger-dialog__footer-actions">
        <HpsfirButton
          v-if="!hideCancel"
          variant="outline"
          :loading="cancelLoading"
          @click="handleCancel"
        >
          {{ textCancel || '取消' }}
        </HpsfirButton>
        <HpsfirButton
          v-if="!hideOk"
          variant="destructive"
          :disabled="!canConfirm"
          :loading="okLoading"
          @click="handleOk"
        >
          {{ textOk || '确认' }}
        </HpsfirButton>
      </div>
    </template>
  </HpsfirProDrawer>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  title?: string;
  formatDangderDeleteInfo?:(row: any) => string;
  onOk?: (row: any) => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  textOk?: string;
  textCancel?: string;
  hideOk?: boolean;
  hideCancel?: boolean;
  optName?: string;
}>();

const open = ref(false);
const row = ref<any>(null);
const inputDate = ref('');
const okLoading = ref(false);
const cancelLoading = ref(false);
const inputId = `business-danger-dialog-input-${Math.random()
  .toString(36)
  .slice(2)}`;

const confirmKeyword = computed(() => props.optName || '删除');
const deleteInfo = computed(() => props.formatDangderDeleteInfo?.(row.value) || '');
const summaryLeadText = computed(() => {
  const info = deleteInfo.value.trim();
  if (info) {
    return `${info.replace(/[，。,；;！!？?]+$/u, '')}，请输入`;
  }
  return '此操作执行后数据不可恢复，请输入';
});
const inputPlaceholder = computed(() => `请输入“${confirmKeyword.value}”以继续`);
const canConfirm = computed(() => inputDate.value === confirmKeyword.value);

watch(open, async(value) => {
  if (!value || typeof document === 'undefined') {
    return;
  }
  await nextTick();
  document.getElementById(inputId)?.focus();
});

const handleOk = async() => {
  if (!canConfirm.value || okLoading.value) {
    return;
  }
  okLoading.value = true;
  try {
    await props.onOk?.(row.value);
    open.value = false;
  } finally {
    okLoading.value = false;
  }
};

const handleCancel = async() => {
  if (cancelLoading.value) {
    return;
  }
  cancelLoading.value = true;
  try {
    await props.onCancel?.();
    open.value = false;
  } finally {
    cancelLoading.value = false;
  }
};

defineExpose({
  showDialog: (_row: any) => {
    inputDate.value = '';
    row.value = {
      ...(_row || {}),
    };
    open.value = true;
  },
});
</script>

<style lang="scss" scoped>
.business-danger-dialog__content {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.business-danger-dialog__summary {
  margin: 0;
  color: var(--muted-foreground, #64748b);
  font-size: 0.9375rem;
  line-height: 1.7;
}

.business-danger-dialog__keyword {
  color: var(--destructive, #ef4444);
  font-weight: 700;
}

.business-danger-dialog__input-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem;
  border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
  border-radius: 1rem;
  background: color-mix(in srgb, var(--muted) 40%, transparent);
}

.business-danger-dialog__input-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.business-danger-dialog__field-label {
  color: var(--foreground, #0f172a);
  font-size: 0.875rem;
  font-weight: 600;
}

.business-danger-dialog__field-chip {
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--destructive) 12%, transparent);
  color: var(--destructive, #ef4444);
  font-size: 0.75rem;
  font-weight: 700;
}

.business-danger-dialog__input {
  width: 100%;
}

.business-danger-dialog__hint {
  margin: 0;
  color: var(--muted-foreground, #64748b);
  font-size: 0.75rem;
}

.business-danger-dialog__footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

@media (max-width: 767px) {
  .business-danger-dialog__footer-actions {
    flex-direction: column-reverse;
  }

  .business-danger-dialog__footer-actions :deep(.hpsfir-btn) {
    width: 100%;
  }
}
</style>
